import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  GroupParticipationReportFileDocument,
  GroupParticipationReportFileQuery,
  GroupParticipationReportFileQueryVariables,
  ParticipationAgreementListQuery,
  ParticipationReportFileDocument,
  ParticipationReportFileQuery,
  ParticipationReportFileQueryVariables,
  ParticipationReportFormat,
  ParticipationSummaryCardQuery,
  useParticipationAgreementListQuery,
  useParticipationSummaryCardQuery,
} from 'gqlhooks';
import { saveBase64File } from 'core/utils/fileDownload';
import {
  PARTICIPATION_FILE_UNAVAILABLE_KEY,
  ParticipationLanguage,
  participationPeriodError,
} from 'core/utils/participation';

import { useDofinPermissions } from './useDofinPermissions';

/**
 * ТЗ 0092 — лог участия дофинансированных (вход и выход по Zoom): доменные
 * хуки карточки кандидата, панели дофина на карточке студента и выгрузки
 * группы на вкладке Zoom.
 *
 * Права — `DOFIN_READ_ROLES` (Admin, AM, Sales, Finance). Для прочих ролей
 * запросы ПРОПУСКАЮТСЯ, а не прячутся: панель висит на карточке студента, и
 * отказ прав всплыл бы ошибкой поверх постороннего экрана.
 */

export type IParticipationAgreement = ParticipationAgreementListQuery['fundingParticipationAgreements'][number];

export type IParticipationSummary = NonNullable<
  ParticipationSummaryCardQuery['fundingParticipationSummary']
>;

export type IParticipationLesson = IParticipationSummary['lessons'][number];

export type IParticipationWarning = IParticipationSummary['warnings'][number];

interface IUseParticipationAgreementsOptions {
  candidateId?: string | null;
  studentId?: string | null;
  /** Не спрашивать вовсе (например, у кандидата нет ни договора, ни аккаунта). */
  skip?: boolean;
  /**
   * Карточка студента — `cache-first`: её открывают тысячи раз, и список
   * договоров не должен уходить на сервер на каждом открытии. Карточка
   * кандидата — `cache-and-network` (там с логом работают).
   */
  fetchPolicy?: 'cache-first' | 'cache-and-network';
}

/** Договоры, по которым собирается лог: по кандидату или по студенту. */
export const useParticipationAgreements = ({
  candidateId,
  studentId,
  skip = false,
  fetchPolicy = 'cache-and-network',
}: IUseParticipationAgreementsOptions) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useParticipationAgreementListQuery({
    variables: {
      candidateId: candidateId || null,
      studentId: studentId || null,
    },
    skip: skip || !canRead || (!candidateId && !studentId),
    fetchPolicy,
  });

  const agreements: IParticipationAgreement[] = useMemo(
    () => data?.fundingParticipationAgreements ?? [],
    [data],
  );

  return { agreements, loading, error, refetch, canRead };
};

interface IUseParticipationSummaryOptions {
  agreementId?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

/**
 * Сводка по договору за период. Неверный период (начало позже конца) на
 * сервер не уходит — ошибку показывает форма.
 *
 * ⚠️ Смена периода меняет переменные, и `loading` снова `true`: форму на это
 * время НЕ снимать с экрана (иначе теряется ввод) — только приглушать сводку.
 */
export const useParticipationSummary = ({
  agreementId,
  dateFrom,
  dateTo,
}: IUseParticipationSummaryOptions) => {
  const { canRead } = useDofinPermissions();
  const periodError = participationPeriodError(dateFrom, dateTo);
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useParticipationSummaryCardQuery({
    variables: {
      agreementId: agreementId || '',
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    },
    skip: !canRead || !agreementId || Boolean(periodError),
    fetchPolicy: 'cache-and-network',
  });

  // Пока идёт ответ на новый период, показываем прежнюю сводку приглушённой:
  // пустое место на секунду читалось бы как «данных нет», и блок прыгал бы.
  const source = data ?? (loading ? previousData : undefined);

  return {
    summary: source?.fundingParticipationSummary ?? null,
    loading,
    error,
    refetch,
    periodError,
  };
};

export interface IParticipationDownloadResult {
  success: boolean;
  /** Второй клик, пока первый файл ещё собирается: ничего не делали. */
  skipped?: boolean;
  fileName?: string;
  /** Ключ перевода. */
  errorKey?: string;
  /** Сообщение сервера — только для отладки, на экран не выводится. */
  errorMessage?: string;
}

interface IBase64FileResponse {
  fileName: string;
  contentType: string;
  contentBase64: string;
}

/** Запросить файл и отдать браузеру; любой отказ — ключ «файл недоступен». */
const fetchAndSave = async (
  fetchFile: () => Promise<IBase64FileResponse | null | undefined>,
): Promise<IParticipationDownloadResult> => {
  try {
    const file = await fetchFile();
    if (!file) {
      return { success: false, errorKey: PARTICIPATION_FILE_UNAVAILABLE_KEY };
    }
    saveBase64File(file);
    return { success: true, fileName: file.fileName };
  } catch (e) {
    return {
      success: false,
      errorKey: PARTICIPATION_FILE_UNAVAILABLE_KEY,
      errorMessage: (e as Error).message,
    };
  }
};

/**
 * Общая часть обоих скачиваний: одна загрузка за раз, флаг занятости, сохранение
 * файла и никаких записей в кеш.
 *
 * 🔴 `no-cache`, а не `network-only`: `network-only` тоже пишет ответ в кеш,
 * и мегабайты base64 с именами и временем входа человека оседали бы в
 * ROOT_QUERY на всю сессию. Файл берётся ПО КЛИКУ и в кеш не попадает.
 */
const useBase64Download = <TKey extends string>() => {
  const [downloading, setDownloading] = useState<TKey | null>(null);
  /** Итог последнего скачивания — для строки `aria-live` под кнопками. */
  const [
    lastResult,
    setLastResult,
  ] = useState<IParticipationDownloadResult | null>(null);
  // Флаг в ref, а не только в state: двойной клик приходит раньше, чем React
  // успевает перерисовать кнопку как `disabled`.
  const busyRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const run = useCallback(
    async (
      key: TKey,
      fetchFile: () => Promise<IBase64FileResponse | null | undefined>,
    ): Promise<IParticipationDownloadResult> => {
      if (busyRef.current) return { success: false, skipped: true };
      busyRef.current = true;
      setDownloading(key);
      setLastResult(null);
      const result = await fetchAndSave(fetchFile);
      busyRef.current = false;
      // Карточку могли закрыть, пока файл собирался.
      if (mountedRef.current) {
        setDownloading(null);
        setLastResult(result);
      }
      return result;
    },
    [],
  );

  return { run, downloading, lastResult };
};

export interface IParticipationReportRequest {
  agreementId: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  format: ParticipationReportFormat;
  language: ParticipationLanguage;
}

/** Скачать лог участия студента по договору (XLSX или PDF). */
export const useParticipationReportDownload = () => {
  const client = useApolloClient();
  const {
    run,
    downloading,
    lastResult,
  } = useBase64Download<ParticipationReportFormat>();

  const download = useCallback(
    ({
      agreementId,
      dateFrom,
      dateTo,
      format,
      language,
    }: IParticipationReportRequest) =>
      run(format, async () => {
        const result = await client.query<
          ParticipationReportFileQuery,
          ParticipationReportFileQueryVariables
        >({
          query: ParticipationReportFileDocument,
          variables: {
            agreementId,
            dateFrom: dateFrom || null,
            dateTo: dateTo || null,
            format,
            language,
          },
          fetchPolicy: 'no-cache',
        });
        return result.data?.fundingParticipationReport;
      }),
    [client, run],
  );

  return { download, downloading, lastResult };
};

export interface IGroupParticipationReportRequest {
  groupId: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  language: ParticipationLanguage;
}

/** Выгрузка группы: XLSX, лист на каждого дофинансированного. */
export const useGroupParticipationReportDownload = () => {
  const client = useApolloClient();
  const { run, downloading, lastResult } = useBase64Download<'group'>();

  const download = useCallback(
    ({
      groupId,
      dateFrom,
      dateTo,
      language,
    }: IGroupParticipationReportRequest) =>
      run('group', async () => {
        const result = await client.query<
          GroupParticipationReportFileQuery,
          GroupParticipationReportFileQueryVariables
        >({
          query: GroupParticipationReportFileDocument,
          variables: {
            groupId,
            dateFrom: dateFrom || null,
            dateTo: dateTo || null,
            language,
          },
          fetchPolicy: 'no-cache',
        });
        return result.data?.groupParticipationReport;
      }),
    [client, run],
  );

  return { download, downloading: downloading === 'group', lastResult };
};
